import { z } from 'zod';
import { ManagedWorkflowLevelSchema } from './managed-workflow.js';
/**
 * Daily Brief contracts (DEV-17472 gather, DEV-17473 compose / feedback / dig).
 *
 * Vocabulary:
 *   - level:   `org | group | device` (UI copy says "robot" for `device`).
 *   - pack:    the stored output of one gather cycle (`brief_pack`): a small
 *              `directory` every request path reads and a big `data` blob
 *              read only when a page has to be composed.
 *   - page:    the composed document the Coherence module renders
 *              (`brief_page.data`).
 *   - context: what one page is about — `org`, `group:<id>` or `device:<id>`.
 *
 * Entity hierarchy: a page only ever talks DOWNWARDS. An org page mixes
 * org-wide, group and robot headlines; a group page covers the group and the
 * robots in it; a robot page covers that robot alone. Every headline names its
 * `subject`, and the page lists the `entities` it mentions so the module can
 * link to them. The server enforces the rule; these shapes only carry it.
 */
export const BriefLevelSchema = ManagedWorkflowLevelSchema;
/** `org` | `group:<groupId>` | `device:<deviceId>` (Formant ids are lowercase UUIDs). */
export const BriefContextKeySchema = z
    .string()
    .regex(/^(org|group:[0-9a-f-]{36}|device:[0-9a-f-]{36})$/);
/** Split a context key into its level and entity id (`null` for `org`). */
export function parseBriefContextKey(contextKey) {
    if (contextKey === 'org')
        return { level: 'org', id: null };
    const sep = contextKey.indexOf(':');
    const level = contextKey.slice(0, sep);
    if (level !== 'group' && level !== 'device') {
        throw new Error(`invalid Daily Brief context key: ${contextKey}`);
    }
    return { level, id: contextKey.slice(sep + 1) };
}
/** Rank used by the downward-only rule: a subject may never rank above its page's context. */
export const briefLevelRank = { org: 0, group: 1, device: 2 };
export const BriefSeveritySchema = z.enum(['good', 'info', 'watch', 'problem']);
export const BriefChartTypeSchema = z.enum(['line', 'area', 'bar', 'hbar', 'stacked', 'pie']);
export const BriefGoodWhenSchema = z.enum(['up', 'down', 'neutral']);
export const BriefRollupSchema = z.enum(['sum', 'mean', 'min', 'max']);
/** Which identity read the data behind a pack (`viewer` = a dig or viewer-run gather). */
export const BriefIdentitySourceSchema = z.enum([
    'metaphysics-mint',
    'connected-admin',
    'dev-login',
    'viewer',
]);
export const BriefTriggerSchema = z.enum([
    'scheduled',
    'first run',
    'refresh',
    'end of day',
    'catch-up',
    'feedback',
]);
// ─── Entities: who a headline is about, and who a page mentions ──────────────
/**
 * Who one headline is about. `id` is null exactly when `level` is `org`.
 * A subject is always at or below the page's context level and inside it.
 */
export const BriefSubjectSchema = z
    .object({
    level: BriefLevelSchema,
    id: z.string().min(1).nullable(),
    name: z.string().min(1),
})
    .refine((s) => (s.level === 'org') === (s.id === null), {
    message: 'subject.id must be null for the org level and set for group/device',
});
/**
 * A group or robot named somewhere on the page (text or tables). The module
 * turns exact occurrences of `name` into links; ids never appear in prose.
 */
export const BriefEntitySchema = z.object({
    level: z.enum(['group', 'device']),
    id: z.string().min(1),
    name: z.string().min(1),
});
// ─── Plan: the analyse node's structured output ──────────────────────────────
const planKey = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/);
export const BriefPlanMetricSchema = z.object({
    key: planKey.describe('snake_case metric key, stable across runs for the same measure'),
    label: z.string().describe('Short label, e.g. "Missions completed"'),
    sql: z
        .string()
        .describe('Returns ONE ROW PER ROBOT: columns device_id, value (number), optional previous (number, same measure for the comparison window). Must contain {{scope}}. ONE plain SELECT with GROUP BY device_id: no sub-queries, WITH, joins or window functions.'),
    unit: z
        .string()
        .describe('Only a symbol unit: "%", "min", "h", "km", "°C", or "" for plain counts. Never a noun.'),
    decimals: z.number().int().min(0).max(2).optional(),
    goodWhen: BriefGoodWhenSchema,
    // No "run this SQL per group" option: a group's value is only ever combined
    // from its own robots' rows, so it cannot hold data from outside the group.
    rollup: BriefRollupSchema.describe('How per-robot values combine into a group / fleet value.'),
});
export const BriefPlanSeriesSchema = z.object({
    key: planKey,
    // No group scope: group charts are rolled up from the robots' rows (see `rollup`).
    scope: z
        .enum(['device', 'org'])
        .describe('device: the SQL returns device_id on every row (ONE plain SELECT with GROUP BY device_id, no sub-queries, joins or window functions) and runs once for the whole fleet; each group and the fleet are combined from those rows. org: a whole-fleet chart with no per-robot or per-group version.'),
    title: z.string(),
    type: BriefChartTypeSchema,
    sql: z
        .string()
        .describe('Rows already ordered for display, at most 60 rows per robot (device scope) or 60 in all (org scope), must contain {{scope}}.'),
    x: z.string(),
    y: z.array(z.string()).min(1),
    series: z.string().optional(),
    unit: z.string().optional(),
    rollup: BriefRollupSchema.optional().describe('device scope only: how per-robot rows combine per x bucket for a group or the fleet.'),
    bucket: z
        .enum(['day', 'hour'])
        .optional()
        .describe('When x is a time label: the bucket size. The SQL must then also return the real bucket as a column named `bucket` (toStartOfDay / toStartOfHour in the given tz), so rows are ordered by time and missing buckets filled.'),
    takeaway: z.string().optional(),
});
/**
 * One thing that stands out today. Names a few robots, a few GROUPS, or both:
 * an org page leads with notable groups as well as notable robots, so the
 * analyst must mark the groups it would point a manager at.
 */
export const BriefPlanNotableSchema = z.object({
    deviceNames: z
        .array(z.string())
        .max(20)
        .describe('Robot names this entry is about. Empty when it is about whole groups.'),
    groupNames: z
        .array(z.string())
        .max(10)
        .describe('Group names this entry is about, exactly as listed in the directory. Empty when it is about individual robots.'),
    severity: BriefSeveritySchema,
    reason: z
        .string()
        .describe('One plain sentence, no numbers typed by hand — name the metric keys instead.'),
    metricKeys: z.array(z.string()).max(4),
});
const BriefPlanKpiSchema = z.object({
    label: z.string().describe('Short label, e.g. "Missions completed".'),
    sql: z
        .string()
        .describe('Returns ONE row. Column `value` (number) is required. Optional column `previous` (number) is the same measure for the comparison period.'),
    unit: z
        .string()
        .optional()
        .describe('Only a symbol unit: "%", "min", "h", "km", "°C", or "" for plain counts. Never a noun like "missions"; the label carries that.'),
    decimals: z.number().int().min(0).max(2).optional(),
    goodWhen: BriefGoodWhenSchema.optional().describe('Which direction of change is good.'),
    /** A card written per robot (the metric shape the schema asks for) is combined across the dig's robots by this. */
    rollup: BriefRollupSchema.optional(),
});
const BriefPlanChartSchema = z.object({
    title: z.string(),
    type: BriefChartTypeSchema,
    sql: z.string().describe('Rows already ordered for display, at most 60 rows.'),
    x: z
        .string()
        .describe('Column with the category or the time label (a short preformatted string).'),
    y: z.array(z.string()).min(1).describe('Numeric column(s) to plot.'),
    series: z
        .string()
        .optional()
        .describe('Optional column to split one y column into several series (long format).'),
    unit: z.string().optional(),
    takeaway: z.string().optional().describe('One plain sentence saying what the chart shows.'),
});
/** Dig mode only: one headline that answers the question, every number backed by SQL. */
export const BriefPlanHeadlineSchema = z.object({
    id: z.string().describe('short-kebab-case, stable across runs for the same topic'),
    severity: BriefSeveritySchema,
    title: z
        .string()
        .describe('One plain sentence. Refer to KPI values as {k0}, {k1} (index into this headline\'s kpis) instead of typing numbers. Never type a measure or percentage yourself: every number goes through a KPI; only figures the page already states (given to you as facts) may be repeated. Days may be named ("on Oct 1").'),
    why: z
        .string()
        .describe('One short line: why it matters to the person running this fleet. May use {k0}. No typed measures.'),
    explanation: z
        .string()
        .describe('2-4 plain sentences for the drill-in. May use {k0}. No typed measures: a count, a duration or a percentage is always a KPI token; days may be named.'),
    kpis: z.array(BriefPlanKpiSchema).max(4),
    charts: z.array(BriefPlanChartSchema).max(3),
    robotsSql: z
        .string()
        .optional()
        .describe('Optional. The robots involved: first column `device_id`, then up to 4 readable columns.'),
    eventsSql: z
        .string()
        .optional()
        .describe('Optional. Evidence events: columns time, device_id, message, severity. Newest first, LIMIT 25.'),
    recommendation: z
        .string()
        .optional()
        .describe('What to do next, one sentence. Empty if nothing to do.'),
    suggestedFeedback: z
        .array(z.string())
        .max(3)
        .optional()
        .describe('Things this user might plausibly tell you about this headline, in their words.'),
});
/**
 * One headline the owner's prompt asks for on every page ("always include
 * work completed over time as a line chart of missions per hour by
 * customer"), bound to the series that shows it. Compose writes these first,
 * in this order, and may leave one out only when its series is empty.
 */
// No length bounds here: a bounded string compiles into a grammar the
// structured-output provider refuses as too large; `sanitisePlan` clamps.
export const BriefPlanRequiredHeadlineSchema = z.object({
    ask: z.string().describe("The owner's words for this headline, shortened to one line."),
    seriesKey: z
        .string()
        .describe('Key of the standard or plan series that shows it (e.g. work_by_hour_by_group_24h, uptime_by_robot_ranked, or one of your series keys).'),
});
export const BriefPlanSchema = z.object({
    metrics: z
        .array(BriefPlanMetricSchema)
        .max(4)
        .describe('Fleet-specific measures beyond the standard ones (work completed, uptime, needs-a-person, errors, battery are already provided).'),
    series: z.array(BriefPlanSeriesSchema).max(6),
    notable: z.array(BriefPlanNotableSchema).max(12),
    requiredHeadlines: z
        .array(BriefPlanRequiredHeadlineSchema)
        .max(4)
        .optional()
        .describe("Headlines the owner's prompt asks for on every page, each bound to a series key. Empty when the prompt asks for none."),
    workMetricKey: z
        .string()
        .optional()
        .describe('When this fleet records finished work outside query_task_summary, or its task summaries undercount it (missions reported as events, summaries that never reach a succeeded status): the key of YOUR metric that counts finished work, so it fills the "Work completed" card whenever the standard count has none.'),
    parentTaskMessage: z
        .string()
        .max(200)
        .optional()
        .describe('The task_summary message that identifies a PARENT task record (count-work-once rule), if the fleet has task summaries.'),
    notes: z
        .string()
        .max(900)
        .optional()
        .describe('What you learned about this fleet that the writer should know (data quirks, which measures are meaningful). ≤ 120 words.'),
    headline: BriefPlanHeadlineSchema.optional(),
});
// ─── Pack: gather output ─────────────────────────────────────────────────────
/** One measure for one context. `previous` is the same measure for the comparison window. */
export const BriefMetricValueSchema = z.object({
    value: z.number().nullable(),
    previous: z.number().nullable(),
});
export const BriefRowSchema = z.record(z.string(), z.unknown());
export const BriefPackDeviceSchema = z.object({
    id: z.string(),
    name: z.string(),
    tags: z.record(z.string(), z.string()),
    groupIds: z.array(z.string()),
});
export const BriefPackGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    tagKey: z.string(),
    tagValue: z.string(),
    deviceIds: z.array(z.string()),
});
export const BriefPackMetricSchema = z.object({
    label: z.string(),
    unit: z.string(),
    decimals: z.number().int().optional(),
    goodWhen: BriefGoodWhenSchema,
    rollup: BriefRollupSchema,
    /** `standard` keys are stable across every fleet; `model` keys come from the analyst's plan. */
    source: z.enum(['standard', 'model']),
    /** Computed from other measures' cells (uptime from offline minutes, success rate from the work counts), never queried; re-rolled the same way for a partial reader. */
    derived: z.enum(['uptime-from-offline', 'success-from-work']).optional(),
    /** Never a scorecard card: cited in prose only. */
    noCard: z.boolean().optional(),
    /**
     * The time basis of the value: `today` = the calendar day so far in the
     * briefing's zone (previous = the same hours of yesterday); `last24h` = a
     * rolling day. Standard measures are `today`; an analyst measure written
     * over a rolling day keeps that basis and says so in its label.
     */
    window: z.enum(['today', 'last24h']).optional(),
});
/** Appended to a measure's label when its basis is a rolling day beside calendar-day charts. */
export const briefLast24hLabelSuffix = ' (last 24 h)';
export const BriefPackSeriesCatalogueEntrySchema = z.object({
    title: z.string(),
    type: BriefChartTypeSchema,
    x: z.string(),
    y: z.array(z.string()),
    series: z.string().nullable(),
    unit: z.string(),
    /** Per-y-column rule for combining robot rows per x bucket; null for context-level model series. */
    rollup: z.record(z.string(), BriefRollupSchema).nullable(),
    source: z.enum(['standard', 'model']),
    scope: BriefLevelSchema,
    takeaway: z.string().optional(),
    /**
     * The x axis is a time bucket of this size. `day`: the last 7 calendar days
     * including today, whose bucket is partial. `hour`: the last 24 complete
     * hours, the current hour left out. Absent for a category axis.
     */
    bucket: z.enum(['day', 'hour']).optional(),
});
/**
 * Notable entry as stored: names resolved to ids at gather time. Context-free;
 * compose picks the entries a page may show (downward-only, viewer-visible).
 * An entry carries robots, groups, or both.
 */
export const BriefPackNotableSchema = z.object({
    deviceIds: z.array(z.string()),
    groupIds: z.array(z.string()),
    severity: BriefSeveritySchema,
    reason: z.string(),
    metricKeys: z.array(z.string()),
});
export const BriefPackErrorSchema = z.object({
    where: z.string(),
    sql: z.string(),
    error: z.string(),
});
/** Column `brief_pack.directory` — the small part every request path reads. */
export const BriefPackDirectorySchema = z.object({
    version: z.literal(2),
    orgId: z.string(),
    workflowId: z.string(),
    level: BriefLevelSchema,
    cycleKey: z.string(),
    mode: z.literal('gather'),
    trigger: BriefTriggerSchema,
    generatedAt: z.string(),
    tz: z.string(),
    /** The organization's display name: the org page's context name and org-level subject. */
    orgName: z.string().nullable().optional(),
    identity: z.object({ userId: z.string().nullable(), source: BriefIdentitySourceSchema }),
    devices: z.array(BriefPackDeviceSchema),
    groups: z.array(BriefPackGroupSchema),
    /** uuid → display name for devices, accounts, fleets and teams. */
    names: z.record(z.string(), z.string()),
    metrics: z.record(z.string(), BriefPackMetricSchema),
    /** Rows live in `data.series` under `<contextKey>::<seriesKey>`. */
    seriesCatalogue: z.record(z.string(), BriefPackSeriesCatalogueEntrySchema),
    notable: z.array(BriefPackNotableSchema),
    /** Robots with no health report in the last 7 days (left out of the uptime mean). */
    silentDeviceIds: z.array(z.string()),
    plan: BriefPlanSchema.omit({ headline: true }),
    planFromPackId: z.string().nullable(),
    /** Hash of the analyst instructions the plan was written under (absent on packs from before it was recorded). */
    instructionsHash: z.string().optional(),
    /** False when the owner's prompt forbids comparisons with earlier days: no delta chip anywhere on the page. Absent = allowed. */
    comparisons: z.boolean().optional(),
    /**
     * Minutes of the calendar day (in `tz`) that had passed when the pack was
     * gathered: the basis of every "today so far" measure (offline minutes are
     * out of this many, uptime is 1 - offline / this). Absent on packs from
     * before the calendar-day basis, which were rolling 24-hour packs (1440).
     */
    dayMinutes: z.number().int().min(0).max(1440).optional(),
    errors: z.array(BriefPackErrorSchema),
    stats: z.object({
        queries: z.number(),
        ms: z.number(),
        modelQueries: z.number(),
        reused: z.boolean(),
        planReused: z.boolean(),
    }),
    truncated: z.boolean(),
});
export const BriefPackEventSchema = z.object({
    time: z.string(),
    device_id: z.string(),
    message: z.string(),
    severity: z.string(),
});
/** Column `brief_pack.data` — read only on a compose miss. Every pack fills all three levels. */
export const BriefPackDataSchema = z.object({
    version: z.literal(2),
    scorecard: z.object({
        org: z.record(z.string(), BriefMetricValueSchema),
        group: z.record(z.string(), z.record(z.string(), BriefMetricValueSchema)),
        device: z.record(z.string(), z.record(z.string(), BriefMetricValueSchema)),
    }),
    /** `<contextKey>::<seriesKey>` → rows. Device contexts hold the raw per-robot rows. */
    series: z.record(z.string(), z.array(BriefRowSchema)),
    /** deviceId → at most 25 rows, newest first (raw device_id; compose maps to names). */
    events: z.record(z.string(), z.array(BriefPackEventSchema)),
});
/**
 * The scorecard cards every page of a level shows, in order, skipping any the
 * context has no value for. The same list draws the cards while a page is
 * being prepared and on the page itself (the writer does not pick cards). A
 * robot page carries its needs-a-person flag in place of an error count.
 */
export const briefStandardScorecard = {
    org: ['work_24h', 'uptime_pct_24h', 'offline_min_24h', 'errors_24h'],
    group: ['work_24h', 'uptime_pct_24h', 'offline_min_24h', 'errors_24h'],
    device: ['work_24h', 'uptime_pct_24h', 'offline_min_24h', 'needs_person_now'],
};
/**
 * The four card titles every page of a level shows, in order, whatever the
 * fleet records: a measure the context has no value for is still a card,
 * reading "—". The placeholders while a page is prepared are these same
 * four. The Work card is filled by the standard work count or, when the
 * fleet does not record task summaries or they count no finished work while
 * the plan's own finished-work measure (`BriefPlan.workMetricKey`) does, by
 * that measure.
 */
export const briefStandardCardTitles = {
    org: ['Work completed', 'Uptime', 'Minutes offline', 'Errors'],
    group: ['Work completed', 'Uptime', 'Minutes offline', 'Errors'],
    device: ['Work completed', 'Uptime', 'Minutes offline', 'Needs a person'],
};
/** Key of one context's series rows inside `BriefPackData.series`. */
export function briefSeriesKey(contextKey, seriesKey) {
    return `${contextKey}::${seriesKey}`;
}
// ─── Page: the composed document ─────────────────────────────────────────────
export const BriefKpiSchema = z.object({
    label: z.string(),
    unit: z.string(),
    decimals: z.number().int().optional(),
    /** Null only on a standard card the context has no value for (its `text` is "—"). */
    value: z.number().nullable(),
    previous: z.number().nullable(),
    /** The number as shown: value with its symbol unit only ("5,497", "92%", "14 min"; "Yes" / "No" for a flag; "—" for none). */
    text: z.string(),
    /**
     * A yes/no card (Needs a person, on a robot's page): `text` is the fact in
     * words and the module shows it in the card's text style, never as a big
     * number. The page's summary does not repeat it.
     */
    flag: z.boolean().optional(),
    /**
     * Time basis of the value: `today` = the calendar day so far in the page's
     * zone, compared with the same hours of yesterday; `last24h` = a rolling
     * day, which the label then also says. Absent for a flag or a 7-day count.
     */
    window: z.enum(['today', 'last24h']).optional(),
    /** Relative change against the comparison window, for counts and quantities. Absent when there is none, it is nil, or comparisons are not wanted. */
    deltaPct: z.number().optional(),
    /** Absolute change in points, for percentage measures (uptime 42.7 → 46.4 is +3.7 pts, not +8.7%). */
    deltaPts: z.number().optional(),
    /** One short line under the card ("offline all day: 11"). */
    note: z.string().optional(),
    goodWhen: BriefGoodWhenSchema,
    /** Pack metric this KPI was bound from; absent on dig KPIs, which carry their own SQL. */
    metricKey: z.string().optional(),
});
export const BriefChartSchema = z.object({
    title: z.string(),
    type: BriefChartTypeSchema,
    x: z.string(),
    y: z.array(z.string()),
    series: z.string().nullable(),
    unit: z.string(),
    takeaway: z.string(),
    rows: z.array(BriefRowSchema),
    /** Pack series this chart was bound from; absent on dig charts. */
    seriesKey: z.string().optional(),
    /** Time axis: `day` = the last 7 calendar days including today; `hour` = the last 24 complete hours. */
    bucket: z.enum(['day', 'hour']).optional(),
    /** The x label of a bucket still in progress (today on a by-day chart): drawn as partial, never read as a move. */
    partialX: z.string().optional(),
});
export const BriefTableSchema = z.object({
    columns: z.array(z.string()),
    rows: z.array(BriefRowSchema),
});
export const BriefHeadlineSchema = z.object({
    id: z.string(),
    severity: BriefSeveritySchema,
    /** Who this headline is about; never above or outside the page's context. */
    subject: BriefSubjectSchema,
    titleText: z.string(),
    whyText: z.string(),
    explanationText: z.string(),
    kpis: z.array(BriefKpiSchema),
    charts: z.array(BriefChartSchema),
    robots: BriefTableSchema.nullable(),
    events: BriefTableSchema.nullable(),
    recommendation: z.string(),
    suggestedFeedback: z.array(z.string()),
    origin: z.literal('dig').optional(),
    askedBy: z.string().optional(),
    question: z.string().optional(),
});
export const BriefPageScopeSchema = z.object({
    robotCount: z.number().int(),
    /** Null for a viewer who cannot see every robot of the context (the total is not disclosed). */
    totalInContext: z.number().int().nullable(),
    note: z.string().nullable(),
});
/** Stored in `brief_page.data`. */
export const BriefPageDataSchema = z.object({
    /** `BriefPage.id`; receipts reference it as `runId`. */
    id: z.string(),
    /** When the DATA was gathered (the pack's `generatedAt`), not when the page was written. */
    finishedAt: z.string(),
    composedAt: z.string(),
    trigger: z.enum([...BriefTriggerSchema.options, 'no-data']),
    summary: z.string(),
    overview: z.array(BriefKpiSchema),
    headlines: z.array(BriefHeadlineSchema),
    /**
     * Every group / robot named in the page's text or tables that the page may
     * mention (at or below its context, inside it, visible to the viewer).
     */
    entities: z.array(BriefEntitySchema),
    level: BriefLevelSchema,
    contextKey: BriefContextKeySchema,
    contextName: z.string(),
    scope: BriefPageScopeSchema,
    /** Names of visible robots silent for more than 7 days. */
    silentRobots: z.array(z.string()),
    endOfDay: z.boolean(),
    /**
     * The IANA zone every card and chart on the page is in: cards are the
     * calendar day so far in it, by-day charts its last 7 days, by-hour charts
     * its last 24 complete hours. The module states it once per page ("times
     * in America/Chicago"). Absent on pages from before the calendar-day basis.
     */
    tz: z.string().optional(),
    /**
     * The pack (`BriefPack.id`) the page was composed from. A chip link
     * carries it as `?briefPack=<packId>`, and the page it opens is composed
     * from the same pack when it is the same org's and at most two cycles
     * old, so a robot's uptime is one figure across one click. Absent on pages
     * from before round 3.
     */
    packId: z.string().optional(),
});
/** The one line a page shows for its time zone. */
export function briefTimeZoneNote(tz) {
    return `times in ${tz}`;
}
// ─── Feedback: receipts, steering, policy ────────────────────────────────────
export const BriefReceiptKindSchema = z.enum(['focus', 'watch', 'dig', 'act', 'noted']);
export const BriefReceiptStatusSchema = z.enum([
    'done',
    'queued',
    'applied',
    'needs-confirm',
    'active',
    'running',
    'answered',
    'sent',
    'failed',
    'undone',
    'dismissed',
    /** Asked about something the asker cannot see: nothing was looked up, nothing was spent. */
    'refused',
]);
/** One dated step in a receipt's life, oldest first, for the module to format in the viewer's locale. */
export const BriefReceiptHistoryEntrySchema = z.object({
    kind: z.enum([
        'created',
        'confirmed',
        'sent',
        'activated',
        'fired',
        'undone',
        'applied',
        'answered',
        'failed',
    ]),
    at: z.string(),
});
export const BriefReceiptSchema = z.object({
    id: z.string(),
    at: z.string(),
    by: z.string(),
    /** The page the feedback was left on (`BriefPageData.id`). */
    runId: z.string().nullable(),
    headlineId: z.string().nullable(),
    /** Title of the headline the feedback was left on, when it was left on one. */
    headlineTitle: z.string().nullable().optional(),
    /**
     * What the person asked, as the page shows it. A name of something another
     * reader cannot see is replaced by "[a group you can't see]" or "[a robot
     * you can't see]" before it is stored.
     */
    text: z.string(),
    kind: BriefReceiptKindSchema,
    understood: z.string(),
    /** Before the request is settled: what will happen. Settled: what happened (the outcome replaces the promise). */
    willDo: z.string(),
    /**
     * When it takes effect, in plain words, computed at read time from the
     * instants on the receipt ("After you confirm", "Active now", "Sent",
     * "From the next update"). Never a stored literal and never a clock time:
     * those are in `history` for the module to format.
     */
    when: z.string(),
    status: BriefReceiptStatusSchema,
    stage: z.string().optional(),
    lastFiredAt: z.string().optional(),
    /** Dated steps, oldest first; kept through Undo. */
    history: z.array(BriefReceiptHistoryEntrySchema).optional(),
    /** Watch: how often at most the rule alerts per robot (fixed default 60 unless the ask named one). */
    intervalMinutes: z.number().int().optional(),
    /**
     * Whether this reader may press Undo (an alert rule that is on) or Confirm.
     * Sent as `false` only when the reader's Formant role can never do it, so
     * the module leaves the button out; absent means Formant decides at click
     * time.
     */
    canUndo: z.boolean().optional(),
    canConfirm: z.boolean().optional(),
    error: z.string().optional(),
    resultHeadlineId: z.string().optional(),
    resultHeadline: BriefHeadlineSchema.optional(),
    /**
     * An answered dig written under an earlier template version whose answer
     * could not be read through the current page binder: the module shows it
     * collapsed in History as "Answer from an earlier version".
     */
    earlierVersion: z.boolean().optional(),
    sent: z.array(z.object({ device: z.string(), commandId: z.string() })).optional(),
    triggerId: z.string().optional(),
    /** Exactly what Confirm will send, so the module can show it before the user agrees. */
    act: z
        .object({
        commandName: z.string(),
        deviceNames: z.array(z.string()),
        parameter: z.string().optional(),
    })
        .optional(),
});
export const BriefSteeringSchema = z.object({
    id: z.string(),
    text: z.string(),
    from: z.string(),
    at: z.string(),
    contextKey: z.string().nullable(),
});
export const BriefPolicySchema = z.object({
    watch: z.enum(['auto', 'confirm', 'off']),
    act: z.enum(['confirm', 'off']),
    /** Command names allowed for Act. null = every enabled template; [] = none. */
    actAllow: z.array(z.string()).nullable(),
    maxOnDemandPerDay: z.number().int(),
    maxComposePerDay: z.number().int(),
});
// ─── Identity status ─────────────────────────────────────────────────────────
/** Why the org has no gather identity; drives the module's not-connected copy. */
export const BriefIdentityReasonSchema = z.enum([
    'not-linked',
    'plan',
    'no-acting-user',
    'rejected',
    'unknown',
]);
export const BriefIdentityStatusSchema = z.object({
    status: z.enum(['ok', 'no-identity']),
    reason: BriefIdentityReasonSchema.nullable(),
});
// ─── Responses ───────────────────────────────────────────────────────────────
export const BriefRunStatusSchema = z.enum([
    'idle',
    'pending',
    'running',
    'failed',
    'no-identity',
    'paused',
    'never',
]);
/**
 * Why the page shown is not a fresh one, when no gather is to blame.
 * `compose-capped`: today's page limit is used up. `compose-failed`: writing
 * the new page failed and the one that exists is shown instead.
 */
export const BriefPageNoteSchema = z.enum(['compose-capped', 'compose-failed']);
/**
 * Why no gather can start for a briefing right now, named after the outcome
 * "Update now" (`POST /v1/daily-brief/refresh`) answers with in that state.
 * `skipped-disabled`: the briefing, its schedule or every schedule is paused.
 * `skipped-breaker`: updates were stopped after repeated failures.
 */
export const BriefGatherBlockedSchema = z.enum(['skipped-disabled', 'skipped-breaker']);
/** `GET /v1/daily-brief/page`, and the body of the `page` compose frame. */
export const BriefPageResponseSchema = z.object({
    page: BriefPageDataSchema.nullable(),
    /** A newer pack exists than the page's, or a compose is in flight for this key. */
    updating: z.boolean(),
    packCycle: z.string().nullable(),
    latestPackCycle: z.string().nullable(),
    scopeHash: z.string(),
    run: z.object({
        status: BriefRunStatusSchema,
        stage: z.string().nullable(),
        startedAt: z.string().nullable(),
        lastError: z.object({ at: z.string(), message: z.string() }).nullable(),
        nextGatherAt: z.string().nullable(),
        /** No pack has ever been written for this workflow. */
        firstRun: z.boolean(),
        retryAfterSec: z.number().int().nullable(),
        note: BriefPageNoteSchema.nullable(),
        /**
         * Set while no gather can start, so nothing newer is on its way: the
         * module shows the page it has and never an "Updating" line. Null when
         * gathers run as usual.
         */
        blocked: BriefGatherBlockedSchema.nullable(),
    }),
    schedule: z
        .object({
        status: z.enum(['ACTIVE', 'PAUSED', 'DISABLED']),
        lastError: z.string().nullable(),
        nextRunAt: z.string().nullable(),
        consecutiveFailures: z.number().int(),
    })
        .nullable(),
    identity: BriefIdentityStatusSchema,
    feed: z.object({ cadenceMin: z.number(), digestTime: z.string(), tz: z.string() }).nullable(),
    steering: z.array(BriefSteeringSchema),
    receipts: z.array(BriefReceiptSchema),
    policy: BriefPolicySchema,
    workflowId: z.string(),
    disabled: z.boolean(),
});
export const RegisterBriefResponseSchema = z.object({
    workflowId: z.string().nullable(),
    scheduleId: z.string().nullable(),
    created: z.boolean(),
    status: z.enum(['ready', 'gathering', 'paused', 'no-identity']),
    identity: BriefIdentityStatusSchema,
    firstRunId: z.string().nullable(),
    /**
     * The title the module shows: the name saved on the view, or "Daily Brief"
     * when none is saved or the saved one is the old default ("Fleet insights").
     */
    title: z.string(),
    /**
     * Labels of the scorecard cards a page of this briefing's level opens with,
     * in order: the standard set less any measure the fleet has no value for
     * (read from the newest pack in the org). Absent before any pack exists,
     * when the module shows the standard set.
     */
    cards: z.array(z.string()).optional(),
});
// ─── Request bodies ──────────────────────────────────────────────────────────
/** Unknown keys (an older module's `prompt`, `settings`, …) are stripped and never read. */
export const RegisterBriefInputSchema = z.object({
    viewId: z.string().min(1).max(200),
    viewName: z.string().min(1).max(200),
    moduleNodeId: z.string().min(1).max(200),
    level: BriefLevelSchema,
    tz: z.string().min(1).max(100).optional(),
});
export const RefreshBriefInputSchema = z.object({ workflowId: z.string().min(1) });
export const ComposeBriefInputSchema = z.object({
    workflowId: z.string().min(1),
    contextKey: BriefContextKeySchema,
    /** Hint only; never part of the cache key. */
    viewerDeviceIds: z.array(z.string().uuid()).max(2000).optional(),
    force: z.boolean().optional(),
    tz: z.string().min(1).max(100).optional(),
    /**
     * The pack a chip link carries (`?briefPack=`): compose from it when it is
     * this org's and at most two cycles old, else from the newest as usual.
     */
    packId: z.string().min(1).max(100).optional(),
});
export const BriefPageQuerySchema = z.object({
    workflowId: z.string().min(1),
    contextKey: BriefContextKeySchema,
    /** The pack a chip link carries (`?briefPack=`); see `ComposeBriefInputSchema.packId`. */
    packId: z.string().min(1).max(100).optional(),
});
export const BriefFeedbackInputSchema = z.object({
    workflowId: z.string().min(1),
    contextKey: BriefContextKeySchema,
    pageId: z.string().min(1).optional(),
    headlineId: z.string().min(1).optional(),
    text: z.string().min(1).max(2000),
});
export const BriefDigInputSchema = z.object({
    workflowId: z.string().min(1),
    contextKey: BriefContextKeySchema,
    pageId: z.string().min(1).optional(),
    headlineId: z.string().min(1).optional(),
    question: z.string().min(1).max(1000),
});
export const BriefReceiptActionInputSchema = z.object({ workflowId: z.string().min(1) });
// ─── Compose stream ──────────────────────────────────────────────────────────
export const BriefStageNameSchema = z.enum([
    'checking',
    'reading',
    'writing',
    'writing-long',
    'charting',
    'verifying',
    'busy',
]);
/** Stage copy, shown verbatim by the module. `{N}` = robot count. */
export const briefStageCopy = {
    checking: 'Checking what is ready',
    reading: "Reading {N} robots' day",
    writing: 'Writing headlines',
    'writing-long': 'Still writing. Almost there.',
    charting: 'Picking charts',
    verifying: 'Checking every number',
    busy: 'Another viewer is opening this briefing. One moment.',
};
/** `reading` copy for a single-robot context. `{name}` = robot name. */
export const briefStageReadingDeviceCopy = "Reading {name}'s day";
export const briefErrorCodes = [
    'disabled',
    'query-token-not-allowed',
    'formant-token-required',
    'workflow-not-found',
    'not-a-brief-workflow',
    'bad-context',
    'bad-level',
    'bad-tz',
    'view-not-found',
    'module-not-on-view',
    'too-many-briefs',
    'context-not-found',
    'context-pending',
    'empty-scope',
    'scope-unavailable',
    'module-disabled',
    'pack-missing',
    'no-identity',
    'busy',
    'too-fast',
    'budget',
    'fire-too-soon',
    'model-failed',
    'model-slow',
    'empty-page',
    'compose-disabled',
    'receipt-not-found',
    'bad-transition',
    'not-allowed',
    'formant-failed',
];
export const BriefErrorCodeSchema = z.enum(briefErrorCodes);
/** JSON error body before a stream starts, and the payload of an `error` frame. */
export const BriefErrorSchema = z.object({
    code: BriefErrorCodeSchema,
    message: z.string(),
    retryAfterSec: z.number().int().optional(),
    reason: BriefIdentityReasonSchema.optional(),
});
/**
 * One SSE frame of `POST /v1/daily-brief/compose` (`data: <json>\n\n`).
 *
 * A stream ends in exactly one of two ways: `page` then `done`, or `error`.
 * When writing a fresh page failed but an older one exists, that is a `page`
 * (with `run.note: 'compose-failed'`) and `done`, never a page and an error.
 */
export const BriefComposeFrameSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('stage'), name: BriefStageNameSchema, copy: z.string() }),
    z.object({
        type: z.literal('scorecard'),
        overview: z.array(BriefKpiSchema),
        packFinishedAt: z.string(),
        contextName: z.string(),
        scope: BriefPageScopeSchema,
    }),
    BriefPageResponseSchema.extend({ type: z.literal('page') }),
    z.object({ type: z.literal('done'), cached: z.boolean(), ms: z.number() }),
    BriefErrorSchema.extend({ type: z.literal('error') }),
    z.object({ type: z.literal('ping') }),
]);
// ─── Dig dedup ───────────────────────────────────────────────────────────────
/**
 * The form two asks are compared in: two digs that normalize the same, asked
 * on the same pack and page, are one dig, and the second returns the first's
 * receipt instead of starting another run. Case, punctuation, spacing and the
 * module's "Dig deeper into:" lead-in do not make a different ask.
 */
export function normalizeBriefDigAsk(question) {
    return question
        .toLowerCase()
        .replace(/^\s*(?:dig (?:deeper )?into|look into|tell me (?:more )?about)\s*:?\s*/, '')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
// ─── Dig credential ──────────────────────────────────────────────────────────
/**
 * Vault KV path (relative to the mount) of the viewer token stashed for one
 * dig. A dig reads data as the person who asked, never as the org's gather
 * identity; the payload is a `ChatActorCredentialSchema` and is deleted when
 * the run ends.
 */
export function briefViewerCredentialVaultPath(orgId, receiptId) {
    return `box/daily-brief-credential/org/${encodeURIComponent(orgId)}/receipt/${encodeURIComponent(receiptId)}`;
}
//# sourceMappingURL=daily-brief.js.map